import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { Slide, toast } from "react-toastify";
import { Autocomplete } from "@mui/material";
import "../../../style/setup.css"; 

const BootstrapButton = styled(Button)({
  boxShadow: "none",
  textTransform: "none",
  fontSize: 14,
  padding: "5px 10px",
  border: "1px solid",
  width: "fit-content",
  height: "100%",
  lineHeight: 1.5,
  backgroundColor: "#069DAE",
  fontFamily: ["Roboto"].join(","),
  "&:hover": {
    backgroundColor: "#0696A6",
    boxShadow: "none",
  },
  "&:active": {
    boxShadow: "none",
    backgroundColor: "#0062cc",
    borderColor: "#005cbf",
  },
  "&:focus": {
    boxShadow: "0 0 0 0.2rem rgba(0,123,255,.5)",
  },
});

export function CellCodeMapping({
  editDataId,
  editedData,
  setEditedData,
  editModalOpen,
  isLoading,
  setIsLoading,
  handleCloseEditModal,
}) {
  const accessToken = Cookies.get("access_token") || "";

  const [dataPanel, setDataPanel] = useState([]);
  const [dataAreaCellEdit, setDataAreaCellEdit] = useState([]);
  const [dataStoreTypes, setDataStoreTypes] = useState([]);
  const [dataCellsEdit, setDataCellsEdit] = useState([]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchPanel();
      fetchStoreType();
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [accessToken]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchAreaCellEdit();
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [editedData.panel]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCellEdit();
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [editedData.panel, editedData.areacell, editedData.storetypecode]);

  async function fetchPanel() {
    const token = Cookies.get("access_token");

    setDataPanel([]);
    setIsLoading(true);

    const queryParams = new URLSearchParams();

    const queryString = queryParams.toString();
    const apiUrl =
      `${process.env.REACT_APP_API_URL}/api/v1/panel` +
      (queryString ? `?${queryString}` : "");

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const { data } = await response.json();
        const newArray = data.map((item) => ({
          label: item.panel,
          value: item.panel,
        }));
        setDataPanel(newArray);
      } else {
        setDataPanel([]);
      }
    } catch (error) {
      setDataPanel([]);
    }
    setIsLoading(false);
  }

  async function fetchStoreType() {
    const token = Cookies.get("access_token");

    setDataStoreTypes([]);
    setIsLoading(true);

    const queryParams = new URLSearchParams();

    const queryString = queryParams.toString();
    const apiUrl =
      `${process.env.REACT_APP_API_URL}/api/v1/stat/store-type` +
      (queryString ? `?${queryString}` : "");

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const { data } = await response.json();
        const newArray = data.map((item) => ({
          label: item.storetype,
          value: item.storetypecode,
        }));
        setDataStoreTypes(newArray);
      } else {
        setDataStoreTypes([]);
      }
    } catch (error) {
      setDataStoreTypes([]);
    }
    setIsLoading(false);
  }

  async function fetchAreaCellEdit() {
    if (editedData.panel) {
      const token = Cookies.get("access_token");

      setDataAreaCellEdit([]);

      const queryParams = new URLSearchParams();

      if (editedData.panel) {
        queryParams.append("panel", editedData.panel);
      }

      const queryString = queryParams.toString();
      const apiUrl =
        `${process.env.REACT_APP_API_URL}/api/v1/stat/areacell` +
        (queryString ? `?${queryString}` : "");

      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const { data } = await response.json();
          const newArray = data.map((item) => ({
            label: `${item.areacell} - ${item.areacelldesc}`,
            value: item.areacell,
          }));
          setDataAreaCellEdit(newArray);
        } else {
          setDataAreaCellEdit([]);
        }
      } catch (error) {
        setDataAreaCellEdit([]);
      }
    }
  }

  async function fetchCellEdit() {
    if (editedData.panel) {
      const token = Cookies.get("access_token");

      setDataCellsEdit([]);

      const queryParams = new URLSearchParams();

      if (editedData.panel) {
        queryParams.append("panel", editedData.panel);
      }

      const queryString = queryParams.toString();
      const apiUrl =
        `${process.env.REACT_APP_API_URL}/api/v1/stat/cell` +
        (queryString ? `?${queryString}` : "");

      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const { data } = await response.json();
          const newArray = data.map((item) => ({
            label: item.cell_desc,
            value: item.cellcode,
          }));

          if (data.length > 0) {
            const found = data.find(
              (item) => item.cellcode === editedData.cellcode
            );

            setEditedData((prev) => ({
              ...prev,
              cellcode: found ? found.cellcode : data[0].cellcode,
            }));
          } else {
            setEditedData((prev) => ({
              ...prev,
              cellcode: "0",
            }));
          }

          setDataCellsEdit(newArray);
        } else {
          setDataCellsEdit([]);
          setEditedData((prev) => ({ ...prev, cellcode: "0" }));
        }
      } catch (error) {
        setDataCellsEdit([]);
      }
    }
  }

  const handleUpdateData = async () => {
    setIsLoading(true);
    const token = Cookies.get("access_token");
    const queryParams = new URLSearchParams();

    const queryString = queryParams.toString();

    if (editDataId) {
      const apiUrl =
        `${process.env.REACT_APP_API_URL}/api/v1/stat/map/cellcode/${editDataId}` +
        (queryString ? `?${queryString}` : "");
      try {
        const response = await fetch(apiUrl, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editedData),
        });

        if (response.status === 200) {
          toast.success("Data berhasil diubah!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
          handleCloseEditModal();
        } else if (
          response.status == 400 ||
          response.status == 403 ||
          response.status == 404
        ) {
          const data = await response.json();
          toast.error(data.detail || "Data gagal diubah!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
        } else if (response.status == 422) {
          toast.error("Data gagal diproses!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
        } else {
          const data = await response.json();
          toast.error(data.detail || "Data gagal diubah!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
        }
      } catch (error) {
        console.error(error);
      }
    } else if (!editDataId) {
      const apiUrl =
        `${process.env.REACT_APP_API_URL}/api/v1/stat/map/cellcode` +
        (queryString ? `?${queryString}` : "");

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editedData),
        });

        if (response.status === 200 || response.status === 201) {
          toast.success("Data berhasil dibuat!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
          window.location.reload();
        } else if (response.status == 403 || response.status == 404) {
          const data = await response.json();
          toast.error(data.detail || "Data gagal dibuat!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
        } else if (response.status == 400) {
          const data = await response.json();
          toast.error(data.detail || "Data gagal dibuat!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
        } else if (response.status == 422) {
          toast.error("Data gagal diproses!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
        } else {
          const data = await response.json();
          toast.error(data.detail || "Data gagal dibuat!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Slide,
          });
        }
      } catch (error) {
        console.error(error);
      }
    }
    setIsLoading(false);
  };

  return (
    <div className="edit-data">
      <div>
        <Autocomplete
          options={dataPanel}
          getOptionLabel={(option) => option.label}
          value={
            dataPanel.find((option) => option.value === editedData.panel) ||
            null
          }
          disabled
          onChange={(event, newValue) => {
            setEditedData({
              ...editedData,
              panel: newValue ? newValue.value : "",
            });
          }}
          sx={{ mb: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="PANEL"
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable
        />
        <Autocomplete
          options={dataAreaCellEdit}
          getOptionLabel={(option) => option.label}
          value={
            dataAreaCellEdit.find(
              (option) => option.value === editedData.areacell
            ) || null
          }
          onChange={(event, newValue) => {
            setEditedData({
              ...editedData,
              areacell: newValue ? newValue.value : "",
            });
          }}
          sx={{ mb: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="AREA CELL"
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable
        />
        <Autocomplete
          options={dataStoreTypes}
          getOptionLabel={(option) => option.label}
          value={
            dataStoreTypes.find(
              (option) => option.value === editedData.storetypecode
            ) || null
          }
          onChange={(event, newValue) => {
            setEditedData({
              ...editedData,
              storetypecode: newValue ? newValue.value : "",
            });
          }}
          sx={{ mb: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="STORE TYPE STAT"
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable
        />
        <Autocomplete
          options={dataCellsEdit}
          getOptionLabel={(option) => option.label}
          value={
            dataCellsEdit.find(
              (option) => option.value === editedData.cellcode
            ) || null
          }
          onChange={(event, newValue) => {
            setEditedData({
              ...editedData,
              cellcode: newValue ? newValue.value : "",
            });
          }}
          sx={{ mb: 2 }}
          renderInput={(params) => <TextField {...params} label="CELL" />}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable
        />
      </div>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="outlined"
          disableRipple
          sx={{
            width: 190,
            height: 50,
            mt: 2,
            color: "#b34469",
            borderColor: "#b34469",
          }}
          onClick={handleCloseEditModal}
        >
          Cancel
        </Button>
        <BootstrapButton
          variant="contained"
          disableRipple
          sx={{ width: 190, height: 50, mt: 2, mr: 0, ml: 2 }}
          onClick={handleUpdateData}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "UPDATE"}
        </BootstrapButton>
      </div>
    </div>
  );
}
